import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FiCalendar, FiPlus, FiBarChart2, FiRefreshCw, FiFilter,
  FiUsers, FiBookOpen, FiTrendingUp, FiLock, FiUnlock, FiTrash2
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { reuniaoJovensService, congregacaoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './ReuniaoJovens.css';

// Extrai a data ignorando timezone (evita shift UTC-3 → dia anterior)
const parseLocalDate = (iso) => {
  if (!iso) return null;
  const s = String(iso).substring(0, 10); // "YYYY-MM-DD"
  const [ano, mes, dia] = s.split('-').map(Number);
  return new Date(ano, mes - 1, dia); // local timezone
};

const formatarData = (iso) => {
  const d = parseLocalDate(iso);
  if (!d) return '';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatarDiaSemana = (iso) => {
  const d = parseLocalDate(iso);
  if (!d) return '';
  const dias = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
  return dias[d.getDay()];
};

const ReuniaoJovensList = () => {
  const navigate = useNavigate();
  const { canWriteModule } = useAuth();
  const podeEditar = canWriteModule('reuniao_jovens');

  const [reunioes, setReunioes] = useState([]);
  const [congregacoes, setCongregacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroCong, setFiltroCong] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('pendentes');

  const carregar = async () => {
    try {
      setLoading(true);
      const resp = await reuniaoJovensService.listar();
      setReunioes(resp.data || []);
    } catch {
      toast.error('Erro ao carregar reuniões');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    congregacaoService.getAtivas().then((r) => {
      const nomes = (r?.data || []).map((c) => c.nome || c.nomeCompleto).filter(Boolean);
      setCongregacoes(nomes);
    }).catch(() => {});
  }, []);

  const reunioesFiltradas = useMemo(() => {
    const filtradas = reunioes.filter((r) => {
      if (filtroCong && r.congregacaoNome !== filtroCong) return false;
      if (filtroStatus === 'preenchidas' && (r.totalGeral || 0) === 0) return false;
      if (filtroStatus === 'pendentes' && ((r.totalGeral || 0) > 0 || r.fechado)) return false;
      if (filtroStatus === 'fechadas' && !r.fechado) return false;
      return true;
    });
    // Ordena por data ASC (mais próximas primeiro)
    return [...filtradas].sort((a, b) => {
      const da = String(a.dataReuniao).substring(0, 10);
      const db = String(b.dataReuniao).substring(0, 10);
      return da.localeCompare(db);
    });
  }, [reunioes, filtroCong, filtroStatus]);

  const kpis = useMemo(() => {
    const total = reunioesFiltradas.length;
    const comDados = reunioesFiltradas.filter((r) => (r.totalGeral || 0) > 0).length;
    const totalCompar = reunioesFiltradas.reduce((acc, r) => acc + (r.totalGeral || 0), 0);
    const media = comDados > 0 ? Math.round(totalCompar / comDados) : 0;
    return { total, comDados, totalCompar, media };
  }, [reunioesFiltradas]);

  const excluir = async (id) => {
    if (!window.confirm('Confirma exclusão da reunião?')) return;
    try {
      await reuniaoJovensService.excluir(id);
      toast.success('Reunião excluída');
      carregar();
    } catch {
      toast.error('Erro ao excluir reunião');
    }
  };

  return (
    <div className="rj-page">
      <header className="rj-page-header">
        <div>
          <h1><FiCalendar className="rj-icon-title" /> Reunião de Jovens e Menores</h1>
          <p>Comparecimento, recitativos e análises por congregação</p>
        </div>
        <div className="rj-header-actions">
          <Link to="/reuniao-jovens/dashboard" className="rj-btn rj-btn-ghost">
            <FiBarChart2 /> Dashboard
          </Link>
          <Link to="/reuniao-jovens/agenda" className="rj-btn rj-btn-ghost">
            <FiCalendar /> Agenda
          </Link>
          {podeEditar && (
            <Link to="/reuniao-jovens/nova" className="rj-btn rj-btn-primary">
              <FiPlus /> Nova reunião
            </Link>
          )}
        </div>
      </header>

      {/* KPIs */}
      <div className="rj-kpis">
        <div className="rj-kpi"><FiCalendar /><div><span className="rj-kpi-num">{kpis.total}</span><span className="rj-kpi-lbl">Reuniões</span></div></div>
        <div className="rj-kpi"><FiBookOpen /><div><span className="rj-kpi-num">{kpis.comDados}</span><span className="rj-kpi-lbl">Preenchidas</span></div></div>
        <div className="rj-kpi"><FiUsers /><div><span className="rj-kpi-num">{kpis.totalCompar}</span><span className="rj-kpi-lbl">Compareceram (total)</span></div></div>
        <div className="rj-kpi"><FiTrendingUp /><div><span className="rj-kpi-num">{kpis.media}</span><span className="rj-kpi-lbl">Média / reunião</span></div></div>
      </div>

      {/* Filtros */}
      <div className="rj-filtros">
        <FiFilter />
        <select value={filtroCong} onChange={(e) => setFiltroCong(e.target.value)}>
          <option value="">Todas as congregações</option>
          {congregacoes.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={carregar} className="rj-btn-icone" title="Atualizar"><FiRefreshCw /></button>
      </div>

      {/* Abas de status */}
      <div className="rj-status-tabs">
        {[
          { key: 'pendentes',   label: 'Pendentes'  },
          { key: 'preenchidas', label: 'Preenchidas' },
          { key: 'fechadas',    label: 'Fechadas'    },
          { key: 'todos',       label: 'Todas'       },
        ].map((tab) => (
          <button
            key={tab.key}
            className={`rj-status-tab ${filtroStatus === tab.key ? 'rj-status-tab--ativo' : ''}`}
            onClick={() => setFiltroStatus(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="rj-empty">Carregando…</div>
      ) : reunioesFiltradas.length === 0 ? (
        <div className="rj-empty">
          <FiCalendar size={28} />
          <h3>Nenhuma reunião</h3>
          <p>
            {reunioes.length === 0
              ? 'Use o botão "Agenda" para gerar reuniões automaticamente, ou cadastre uma manual.'
              : 'Nenhuma reunião corresponde aos filtros.'}
          </p>
          {podeEditar && (
            <Link to="/reuniao-jovens/agenda" className="rj-btn rj-btn-primary">
              Configurar agenda
            </Link>
          )}
        </div>
      ) : (
        <div className="rj-cards-grid">
          {reunioesFiltradas.map((r) => {
            const preenchida = (r.totalGeral || 0) > 0;
            return (
              <div key={r.id} className={`rj-card ${r.fechado ? 'rj-card--fechado' : ''}`}>
                <div className="rj-card-top">
                  <div className="rj-card-data">
                    <span className="rj-card-dia">{formatarData(r.dataReuniao)}</span>
                    <span className="rj-card-semana">{formatarDiaSemana(r.dataReuniao)} · {r.horario || '10:00'}</span>
                  </div>
                  {r.fechado ? (
                    <span className="rj-tag rj-tag-fechada"><FiLock size={12} /> Fechada</span>
                  ) : preenchida ? (
                    <span className="rj-tag rj-tag-ok">Preenchida</span>
                  ) : (
                    <span className="rj-tag rj-tag-pendente"><FiUnlock size={12} /> Pendente</span>
                  )}
                </div>

                <div className="rj-card-cong">{r.congregacaoNome}</div>

                {preenchida ? (
                  <div className="rj-card-stats">
                    <div><strong>{r.totalGeral}</strong><span>compareceram</span></div>
                    <div><strong>{r.totalRecitaram}</strong><span>recitaram</span></div>
                    <div>
                      <strong>
                        {r.totalGeral > 0 ? Math.round((r.totalRecitaram / r.totalGeral) * 100) : 0}%
                      </strong>
                      <span>taxa</span>
                    </div>
                  </div>
                ) : (
                  <div className="rj-card-stats rj-card-stats--vazio">
                    Nenhum dado registrado ainda.
                  </div>
                )}

                <div className="rj-card-acts">
                  <button
                    className="rj-btn rj-btn-primary"
                    onClick={() => navigate(`/reuniao-jovens/${r.id}`)}
                  >
                    {preenchida ? 'Ver / Editar' : 'Preencher'}
                  </button>
                  {podeEditar && !r.fechado && (
                    <button
                      className="rj-btn-icone rj-btn-danger"
                      onClick={() => excluir(r.id)}
                      title="Excluir"
                    >
                      <FiTrash2 />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReuniaoJovensList;
